/**
 * apiErrorMessage - Turn a thrown API failure into a sentence a user can act on.
 *
 * Laravel answers a failed write in one of two shapes:
 *   422 → { message, errors: { field: [ "…" ] } }   (validation)
 *   4xx → { message: "…" }                          (authorization, conflicts)
 *
 * The field-level message is the useful one — "Record hours for every
 * assignee — still missing: Jane Doe." says what to do, where the generic
 * `message` ("The given data was invalid.") does not. So the first field
 * error wins, then the top-level message, then the caller's fallback.
 *
 * Every surface that writes through `api` had been re-deriving this by hand;
 * this is the one place it lives now.
 *
 * @example
 * catch (err) {
 *     setError(apiErrorMessage(err, 'Could not save. Please try again.'));
 * }
 */
import { ApiError } from '../services/api';

/** The body shape Laravel returns on a failed request. */
interface ApiErrorBody {
	message?: string;
	errors?: Record<string, string[]>;
}

export function apiErrorMessage(err: unknown, fallback: string): string {
	if (err instanceof ApiError) {
		const body = err.data as ApiErrorBody | undefined;
		/* Validation errors are keyed by field; any one of them is more
		   specific than the envelope message, and the first is the one the
		   user hits first when they go back to fix it. */
		const firstFieldError = body?.errors
			? Object.values(body.errors)[0]?.[0]
			: undefined;
		return firstFieldError || body?.message || fallback;
	}

	/* Network failures and other thrown Errors — their own message is
	   usually noise ("Failed to fetch"), so the caller's copy wins. */
	return fallback;
}

export default apiErrorMessage;
